/**
 * Supported language tags for the application
 */
export const SUPPORTED_LANGUAGE_TAGS = ['en', 'fr', 'de', 'es', 'it', 'pl'];

/**
 * Normalizes a language code to a 2-letter ISO code
 * Converts 5-letter codes (e.g., 'de-DE') to 2-letter codes (e.g., 'de')
 * @param {string} languageCode - The language code to normalize
 * @returns {string} The normalized 2-letter language code
 */
export const normalizeLanguageCode = (languageCode) => {
    if (!languageCode) {
        return 'en';
    }
    
    // Extract the base language (first 2 letters)
    const normalized = languageCode.split('-')[0].toLowerCase();
    
    // Check if it's in our supported languages
    if (SUPPORTED_LANGUAGE_TAGS.includes(normalized)) {
        return normalized;
    }
    
    // Fallback to English
    return 'en';
};

/**
 * Get the display name for a language tag in a specific locale
 * @param {string} languageTag - The language tag (e.g., 'en', 'fr', 'de')
 * @param {string} locale - The locale to display the name in (e.g., 'en', 'de')
 * @returns {string} The localized language name or the language tag as fallback
 */
export const getLanguageDisplayName = (languageTag, locale) => {
    try {
        if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
            const displayNames = new Intl.DisplayNames([locale || 'en'], { type: 'language' });
            const name = displayNames.of(languageTag);
            return name || languageTag;
        }
    } catch {
        // no-op; fall back below
    }
    return languageTag;
};

/**
 * Generate language selection options with localized names
 * @param {string} locale - The locale to display the names in
 * @param {string[]} [languageTags] - Optional array of language tags to include (defaults to SUPPORTED_LANGUAGE_TAGS)
 * @returns {Array<{id: string, text: string}>} Array of language selection objects
 */
export const getLanguageSelectionOptions = (locale, languageTags = SUPPORTED_LANGUAGE_TAGS) => {
    return languageTags.map(tag => ({
        id: tag,
        text: getLanguageDisplayName(tag, locale)
    }));
};